import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import OverviewScreen from './OverviewScreen';
import KYCScreen from './KYCScreen';
import AgeVerificationScreen from './AgeVerificationScreen';
import BillingScreen from './BillingScreen';
import CustomerDetailsScreen from './CustomerDetailsScreen';
import FileDetailsScreen from './FileDetailsScreen';
import { api } from '../utils/api';
import './PortalScreen.css';

function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function DocumentCheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CogIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CreditCardIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

const NAV_ITEMS_ALL = [
  { key: 'overview', label: 'Overview', icon: HomeIcon },
  { key: 'kyc', label: 'KYC', icon: DocumentCheckIcon },
  { key: 'age-verification', label: 'Age Verification', icon: CheckCircleIcon },
  { key: 'billing', label: 'Billing', icon: CreditCardIcon },
  { key: 'settings', label: 'Settings', icon: CogIcon },
];
// KYC and Settings hidden from nav (routes still work for deep links)
const NAV_ITEMS = NAV_ITEMS_ALL.filter((i) => i.key !== 'kyc' && i.key !== 'settings');

function MenuIcon({ isOpen }) {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d={isOpen ? 'M5 5L15 15M15 5L5 15' : 'M3 5H17M3 10H17M3 15H17'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PaymentCompleteRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/billing?payment=success', { replace: true });
  }, [navigate]);
  return (
    <div className="vt-pageHead">
      <h1 className="vt-pageTitle">Payment complete</h1>
      <p className="vt-pageSub vt-muted">Redirecting you to Billing…</p>
    </div>
  );
}

export default function PortalScreen({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userFirstName, setUserFirstName] = useState(null);

  // Determine active tab from route
  const active = useMemo(() => {
    const path = location.pathname;
    if (path === '/' || path === '/overview') return 'overview';
    if (path.startsWith('/kyc') || path.startsWith('/customers/')) return 'overview'; // KYC hidden; show overview as active
    if (path.startsWith('/age-verification')) return 'age-verification';
    if (path.startsWith('/billing') || path.startsWith('/reference/')) return 'billing';
    if (path.startsWith('/settings')) return 'overview'; // Settings hidden from nav
    return 'overview';
  }, [location.pathname]);

  const activeLabel = useMemo(() => NAV_ITEMS_ALL.find((i) => i.key === active)?.label || NAV_ITEMS.find((i) => i.key === active)?.label || 'Overview', [active]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userProfile = await api.getUserProfile();
        if (userProfile) {
          if (userProfile.username) setUserEmail(userProfile.username);
          if (userProfile.firstName) setUserFirstName(userProfile.firstName);
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  return (
    <div className="vt-portal">
      <header className="vt-topbar">
        <div className="vt-topbarLeft">
          <button
            className="vt-iconBtn"
            type="button"
            onClick={() => setIsNavOpen((v) => !v)}
            aria-label={isNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={isNavOpen ? 'true' : 'false'}
          >
            <MenuIcon isOpen={isNavOpen} />
          </button>
        </div>

        <div className="vt-topbarCenter">
          <div className="vt-topbarBrand" aria-label="Veritage dashboard">
            <span className="vt-topbarLogo" aria-hidden="true">
              <svg
                width="48"
                height="48"
                viewBox="0 0 200 200"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g transform="translate(100,100)" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
                  <g>
                    <circle cx="0" cy="0" r="82" fill="none" stroke="none" />
                    {[...Array(20)].map((_, i) => (
                      <line
                        key={`outer-${i}`}
                        x1="0"
                        y1="-82"
                        x2="0"
                        y2="-70"
                        transform={`rotate(${i * 18})`}
                      />
                    ))}
                  </g>
                  <g>
                    <circle cx="0" cy="0" r="60" fill="none" stroke="none" />
                    {[...Array(20)].map((_, i) => (
                      <line
                        key={`inner-${i}`}
                        x1="0"
                        y1="-56"
                        x2="0"
                        y2="-48"
                        transform={`rotate(${i * 18})`}
                      />
                    ))}
                  </g>
                </g>
              </svg>
            </span>
            <div className="vt-topbarBrandText">
              <div className="vt-topbarName">Veritage</div>
              <div className="vt-topbarSub vt-muted">Dashboard</div>
            </div>
          </div>
        </div>

        <div className="vt-topbarRight">
          {userEmail && (
            <span className="vt-topbarEmail vt-muted">{userEmail}</span>
          )}
          <button className="vt-btn vt-btn--secondary" type="button" onClick={onLogout}>
            Log out
          </button>
        </div>
      </header>

      <div className="vt-portalBody">
        <aside className={['vt-sidenav', isNavOpen ? 'vt-sidenav--open' : ''].filter(Boolean).join(' ')}>
          <div className="vt-sidenavInner vt-surface">
            <div className="vt-sidenavBrand">
              <div className="vt-sidenavBrandContent" aria-label="Veritage dashboard">
                <span className="vt-sidenavLogo" aria-hidden="true">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <g transform="translate(100,100)" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
                      <g>
                        <circle cx="0" cy="0" r="82" fill="none" stroke="none" />
                        {[...Array(20)].map((_, i) => (
                          <line
                            key={`outer-${i}`}
                            x1="0"
                            y1="-82"
                            x2="0"
                            y2="-70"
                            transform={`rotate(${i * 18})`}
                          />
                        ))}
                      </g>
                      <g>
                        <circle cx="0" cy="0" r="60" fill="none" stroke="none" />
                        {[...Array(20)].map((_, i) => (
                          <line
                            key={`inner-${i}`}
                            x1="0"
                            y1="-56"
                            x2="0"
                            y2="-48"
                            transform={`rotate(${i * 18})`}
                          />
                        ))}
                      </g>
                    </g>
                  </svg>
                </span>
                <div className="vt-sidenavBrandText">
                  <div className="vt-sidenavName">Veritage</div>
                  <div className="vt-sidenavSub vt-muted">Dashboard</div>
                </div>
              </div>
            </div>
            <nav aria-label="Dashboard navigation" className="vt-sidenavNav">
              {NAV_ITEMS.map((item) => {
                const isActive = item.key === active;
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={['vt-navItem', isActive ? 'vt-navItem--active' : ''].filter(Boolean).join(' ')}
                    onClick={() => {
                      const route = item.key === 'overview' ? '/' : `/${item.key}`;
                      navigate(route);
                      setIsNavOpen(false);
                    }}
                  >
                    <span className="vt-navItemIcon" aria-hidden="true">
                      <IconComponent className="vt-navItemIconSvg" />
                    </span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="vt-sidenavFoot vt-muted">
              <span className="vt-sidenavPill">SOC 2</span>
              <span className="vt-sidenavPill">GDPR</span>
              <span className="vt-sidenavPill">ISO 27001</span>
            </div>
          </div>
        </aside>

        <button 
          className={['vt-overlay', isNavOpen ? 'vt-overlay--visible' : ''].filter(Boolean).join(' ')} 
          type="button" 
          onClick={() => setIsNavOpen(false)} 
          aria-label="Close navigation overlay"
          aria-hidden={!isNavOpen}
        />

        <main className="vt-main">
          <div className="vt-container">
            <Routes>
              <Route path="/" element={<OverviewScreen userEmail={userEmail} firstName={userFirstName} />} />
              <Route path="/overview" element={<OverviewScreen userEmail={userEmail} firstName={userFirstName} />} />
              <Route path="/kyc" element={<KYCScreen />} />
              <Route path="/customers/:customerId" element={<CustomerDetailsScreen />} />
              <Route path="/customers/:customerId/files/:fileId" element={<FileDetailsScreen />} />
              <Route path="/age-verification" element={<AgeVerificationScreen />} />
              <Route path="/billing" element={<BillingScreen />} />
              <Route path="/reference/:referenceId/payment-complete" element={
                <PaymentCompleteRedirect />
              } />
              <Route path="/settings" element={
                <>
                  <div className="vt-pageHead">
                    <h1 className="vt-pageTitle">Settings</h1>
                    <p className="vt-pageSub vt-muted">
                      This is a UI shell. Hook this up to real APIs when ready.
                    </p>
                  </div>
                </>
              } />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

